import React, { useState, useEffect } from 'react';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import { Percent, TrendingUp, DollarSign, Calendar, Download, Plus, BarChart3, Target, Trash2, Loader2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { promoDiscountService } from '../../services/promoDiscountService';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency } from '../../utils/currencyFormatter';

const PromoDiscountImpactCalculator = () => {
  const { profile } = useAuth();
  const [originalPrice, setOriginalPrice] = useState('');
  const [discountType, setDiscountType] = useState('percentage'); // 'percentage' or 'fixed'
  const [discountValue, setDiscountValue] = useState('');
  const [costOfGoods, setCostOfGoods] = useState('');
  const [currentVolume, setCurrentVolume] = useState('');
  const [scenarios, setScenarios] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [loadingCalculations, setLoadingCalculations] = useState(true);

  const userCurrency = profile?.currency || 'ZAR';

  // Load saved calculations
  useEffect(() => {
    loadSavedCalculations();
  }, []);

  const loadSavedCalculations = async () => {
    try {
      setLoadingCalculations(true);
      const calculations = await promoDiscountService?.getAll();
      setSavedCalculations(calculations);
    } catch (err) {
      console.error('Error loading calculations:', err);
    } finally {
      setLoadingCalculations(false);
    }
  };

  const parseNum = (val) => {
    const num = parseFloat(String(val)?.replace(/[^\d.-]/g, ''));
    return isNaN(num) ? 0 : num;
  };

  const formatCurrencyLocal = (amount) => {
    return formatCurrency(parseNum(amount), userCurrency);
  };

  const formatPercent = (value) => {
    return `${parseNum(value)?.toFixed(2)}%`;
  };

  const handleCalculate = () => {
    const price = parseNum(originalPrice);
    const discount = parseNum(discountValue);
    const cogs = parseNum(costOfGoods);
    const volume = parseNum(currentVolume);

    if (price <= 0 || cogs <= 0) {
      setError('Please enter valid original price and cost of goods');
      return;
    }

    let discountedPrice;
    let discountAmount;
    if (discountType === 'percentage') {
      discountAmount = price * (discount / 100);
      discountedPrice = price - discountAmount;
    } else {
      discountAmount = discount;
      discountedPrice = price - discount;
    }

    const originalMargin = ((price - cogs) / price) * 100;
    const discountedMargin = ((discountedPrice - cogs) / discountedPrice) * 100;
    const revenueReduction = ((price - discountedPrice) / price) * 100;
    const breakEvenVolumeIncrease = ((price - discountedPrice) / (discountedPrice - cogs)) * 100;
    
    const originalRevenue = price * volume;
    const discountedRevenue = discountedPrice * volume;
    const originalProfit = (price - cogs) * volume;
    const discountedProfit = (discountedPrice - cogs) * volume;

    const calculationResult = {
      id: Date.now(),
      timestamp: new Date()?.toLocaleTimeString(),
      originalPrice: price,
      discountedPrice,
      discountAmount,
      discountPercentage: ((price - discountedPrice) / price) * 100,
      originalMargin,
      discountedMargin,
      marginImpact: originalMargin - discountedMargin,
      revenueReduction,
      breakEvenVolumeIncrease,
      currentVolume: volume,
      requiredVolume: volume * (1 + breakEvenVolumeIncrease / 100),
      originalRevenue,
      discountedRevenue,
      originalProfit,
      discountedProfit,
      profitImpact: originalProfit - discountedProfit,
    };

    setResult(calculationResult);
    setError('');
  };

  const handlePresetDiscount = (percentage) => {
    setDiscountType('percentage');
    setDiscountValue(percentage?.toString());
  };

  const handleSaveCalculation = async () => {
    if (!result) return;

    try {
      setLoading(true);
      const calculation = {
        promoName: `${discountType === 'percentage' ? `${discountValue}%` : formatCurrencyLocal(discountValue)} Discount`,
        promoType: discountType,
        originalPrice: result?.originalPrice,
        discountPercentage: discountType === 'percentage' ? parseNum(discountValue) : null,
        discountFixedAmount: discountType === 'fixed' ? parseNum(discountValue) : null,
        finalPrice: result?.discountedPrice,
        discountAmount: result?.discountAmount,
        revenueImpact: result?.revenueReduction,
        expectedSalesIncreasePercentage: result?.breakEvenVolumeIncrease,
        projectedUnitsSold: Math.round(result?.requiredVolume),
        projectedRevenue: result?.discountedRevenue,
        validFrom: new Date()?.toISOString(),
        validUntil: null,
      };

      await promoDiscountService?.create(calculation);
      await loadSavedCalculations();
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to save calculation');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCalculation = async (id) => {
    try {
      setLoading(true);
      await promoDiscountService?.delete(id);
      await loadSavedCalculations();
    } catch (err) {
      setError(err?.message || 'Failed to delete calculation');
    } finally {
      setLoading(false);
    }
  };

  const addScenario = () => {
    if (!result) return;
    
    const scenario = {
      ...result,
      name: `${discountType === 'percentage' ? `${discountValue}%` : formatCurrencyLocal(discountValue)} Discount`,
      color: `#${Math.floor(Math.random()*16777215)?.toString(16)}`,
    };
    
    setScenarios(prev => [...prev, scenario]?.slice(-4));
  };

  const clearScenarios = () => {
    setScenarios([]);
  };

  const exportReport = () => {
    const reportData = {
      calculation: result,
      scenarios,
      timestamp: new Date()?.toISOString(),
    };
    
    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = URL?.createObjectURL(blob);
    const link = document?.createElement('a');
    link.href = url;
    link.download = `promo-impact-report-${Date.now()}.json`;
    link?.click();
  };

  const getPerformanceColor = (value, threshold) => {
    if (value > threshold) return 'text-red-600 bg-red-50 border-red-200';
    if (value > threshold / 2) return 'text-orange-600 bg-orange-50 border-orange-200';
    return 'text-green-600 bg-green-50 border-green-200';
  };

  const breakEvenChartData = result ? [
    { name: 'Current', volume: result?.currentVolume, profit: result?.discountedProfit },
    { name: 'Break-Even', volume: result?.requiredVolume, profit: result?.originalProfit },
  ] : [];

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <Header />
      <main className="ml-64 mt-16 p-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <Percent className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Promo Discount Impact Calculator</h1>
                <p className="text-sm text-slate-600">Strategic discount analysis with profitability calculations</p>
              </div>
            </div>
            {result && (
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSaveCalculation}
                  disabled={loading}
                  className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Save Calculation
                </button>
                <button
                  onClick={exportReport}
                  className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Export Report
                </button>
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Sidebar - Quick Presets */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
              <h3 className="text-lg font-bold text-slate-900 mb-4">Quick Discount Presets</h3>
              <div className="space-y-2">
                {[5, 10, 15, 20, 25, 30, 40, 50, 75]?.map(percentage => (
                  <button
                    key={percentage}
                    onClick={() => handlePresetDiscount(percentage)}
                    className="w-full px-4 py-2 rounded-lg border-2 border-slate-200 hover:border-purple-500 hover:bg-purple-50 transition-all text-left"
                  >
                    <span className="font-semibold text-slate-900">{percentage}%</span>
                    <span className="text-xs text-slate-600 block">Discount</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
              <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-purple-600" />
                Seasonal Templates
              </h3>
              <div className="space-y-2">
                <button
                  onClick={() => handlePresetDiscount(15)}
                  className="w-full px-4 py-2 rounded-lg border border-slate-200 hover:border-purple-500 hover:bg-purple-50 transition-all text-left"
                >
                  <span className="font-medium text-slate-900">Black Friday</span>
                  <span className="text-xs text-slate-600 block">15% Standard</span>
                </button>
                <button
                  onClick={() => handlePresetDiscount(20)}
                  className="w-full px-4 py-2 rounded-lg border border-slate-200 hover:border-purple-500 hover:bg-purple-50 transition-all text-left"
                >
                  <span className="font-medium text-slate-900">Cyber Monday</span>
                  <span className="text-xs text-slate-600 block">20% Flash Sale</span>
                </button>
                <button
                  onClick={() => handlePresetDiscount(10)}
                  className="w-full px-4 py-2 rounded-lg border border-slate-200 hover:border-purple-500 hover:bg-purple-50 transition-all text-left"
                >
                  <span className="font-medium text-slate-900">New Customer</span>
                  <span className="text-xs text-slate-600 block">10% Welcome</span>
                </button>
              </div>
            </div>

            {/* Saved Calculations */}
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-4">Saved Calculations</h3>
              {loadingCalculations ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
                </div>
              ) : savedCalculations?.length > 0 ? (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {savedCalculations?.map((calc) => (
                    <div
                      key={calc?.id}
                      className="p-3 border border-slate-200 rounded-lg hover:border-purple-300 transition-all"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-bold text-slate-900">{calc?.promoName}</span>
                        <button
                          onClick={() => handleDeleteCalculation(calc?.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="space-y-1 text-xs text-slate-600">
                        <div className="flex justify-between">
                          <span>Original:</span>
                          <span className="font-medium text-slate-900">{formatCurrencyLocal(calc?.originalPrice)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Final:</span>
                          <span className="font-medium text-slate-900">{formatCurrencyLocal(calc?.finalPrice)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Impact:</span>
                          <span className="font-medium text-orange-700">{formatPercent(calc?.revenueImpact)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-600 text-center py-4">No saved calculations yet</p>
              )}
            </div>
          </div>

          {/* Center - Calculator */}
          <div className="lg:col-span-6">
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
              <h3 className="text-lg font-bold text-slate-900 mb-6">Discount Configuration</h3>

              <div className="space-y-4">
                {/* Original Price */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Original Price</label>
                  <input
                    type="text"
                    value={originalPrice}
                    onChange={(e) => setOriginalPrice(e?.target?.value)}
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    placeholder="Enter original price"
                  />
                </div>

                {/* Discount Type Toggle */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Discount Type</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setDiscountType('percentage')}
                      className={`px-4 py-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                        discountType === 'percentage' ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Percent className="w-4 h-4" />
                      <span className="font-medium">Percentage</span>
                    </button>
                    <button
                      onClick={() => setDiscountType('fixed')}
                      className={`px-4 py-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                        discountType === 'fixed' ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <DollarSign className="w-4 h-4" />
                      <span className="font-medium">Fixed Amount</span>
                    </button>
                  </div>
                </div>

                {/* Discount Value */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Discount {discountType === 'percentage' ? 'Percentage' : 'Amount'}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={discountValue}
                      onChange={(e) => setDiscountValue(e?.target?.value)}
                      className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all pr-10"
                      placeholder={discountType === 'percentage' ? 'Enter percentage' : 'Enter amount'}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500">
                      {discountType === 'percentage' ? '%' : userCurrency}
                    </span>
                  </div>
                </div>

                {/* Cost of Goods */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Cost of Goods Sold (COGS)</label>
                  <input
                    type="text"
                    value={costOfGoods}
                    onChange={(e) => setCostOfGoods(e?.target?.value)}
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    placeholder="Enter cost per unit"
                  />
                </div>

                {/* Current Volume */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Current Sales Volume</label>
                  <input
                    type="text"
                    value={currentVolume}
                    onChange={(e) => setCurrentVolume(e?.target?.value)}
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    placeholder="Enter current units sold"
                  />
                </div>

                {/* Calculate Button */}
                <button
                  onClick={handleCalculate}
                  className="w-full px-6 py-4 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-semibold transition-colors flex items-center justify-center gap-2"
                >
                  <Target className="w-5 h-5" />
                  Calculate Impact
                </button>
              </div>
            </div>

            {/* Results */}
            {result && (
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-bold text-slate-900">Impact Analysis</h3>
                  <button
                    onClick={addScenario}
                    className="px-4 py-2 rounded-lg bg-purple-100 hover:bg-purple-200 text-purple-700 font-medium transition-colors flex items-center gap-2 text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Add to Scenarios
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <span className="text-sm font-medium text-blue-900 block mb-1">Original Price</span>
                    <p className="text-xl font-bold text-blue-700">{formatCurrencyLocal(result?.originalPrice)}</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <span className="text-sm font-medium text-green-900 block mb-1">Discounted Price</span>
                    <p className="text-xl font-bold text-green-700">{formatCurrencyLocal(result?.discountedPrice)}</p>
                  </div>
                  <div className={`p-4 rounded-lg border ${getPerformanceColor(result?.revenueReduction, 30)}`}>
                    <span className="text-sm font-medium block mb-1">Revenue Reduction</span>
                    <p className="text-xl font-bold">{formatPercent(result?.revenueReduction)}</p>
                  </div>
                  <div className={`p-4 rounded-lg border ${getPerformanceColor(result?.breakEvenVolumeIncrease, 50)}`}>
                    <span className="text-sm font-medium block mb-1">Break-Even Volume Increase</span>
                    <p className="text-xl font-bold">{formatPercent(result?.breakEvenVolumeIncrease)}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-slate-700">Profit Margin Impact</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-xs text-slate-600">Original: </span>
                        <span className="text-sm font-bold text-slate-900">{formatPercent(result?.originalMargin)}</span>
                      </div>
                      <TrendingUp className="w-5 h-5 text-slate-400" />
                      <div>
                        <span className="text-xs text-slate-600">Discounted: </span>
                        <span className="text-sm font-bold text-slate-900">{formatPercent(result?.discountedMargin)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <span className="text-sm font-medium text-slate-700 block mb-2">Required Volume for Same Profit</span>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-xs text-slate-600">Current: </span>
                        <span className="text-sm font-bold text-slate-900">{result?.currentVolume?.toLocaleString()} units</span>
                      </div>
                      <div>
                        <span className="text-xs text-slate-600">Required: </span>
                        <span className="text-sm font-bold text-purple-700">{result?.requiredVolume?.toFixed(0)?.toLocaleString()} units</span>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <span className="text-sm font-medium text-orange-900 block mb-2">Profit Impact</span>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-xs text-orange-700">Original Profit: </span>
                        <span className="text-sm font-bold text-orange-900">{formatCurrencyLocal(result?.originalProfit)}</span>
                      </div>
                      <div>
                        <span className="text-xs text-orange-700">Loss: </span>
                        <span className="text-sm font-bold text-red-700">{formatCurrencyLocal(result?.profitImpact)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Sidebar - Analysis */}
          <div className="lg:col-span-3">
            {result && (
              <>
                <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-purple-600" />
                    Break-Even Chart
                  </h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={breakEvenChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip formatter={(value) => formatCurrencyLocal(value)} />
                        <Bar dataKey="volume" fill="#8b5cf6" name="Volume" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {scenarios?.length > 0 && (
                  <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-slate-900">Scenario Comparison</h3>
                      <button
                        onClick={clearScenarios}
                        className="text-sm text-red-600 hover:text-red-700 font-medium"
                      >
                        Clear
                      </button>
                    </div>
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {scenarios?.map((scenario, index) => (
                        <div
                          key={scenario?.id}
                          className="p-3 border border-slate-200 rounded-lg hover:border-purple-300 transition-all"
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: scenario?.color }}
                            />
                            <span className="text-sm font-bold text-slate-900">{scenario?.name}</span>
                          </div>
                          <div className="space-y-1 text-xs text-slate-600">
                            <div className="flex justify-between">
                              <span>Discounted Price:</span>
                              <span className="font-medium text-slate-900">{formatCurrencyLocal(scenario?.discountedPrice)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Break-Even Volume:</span>
                              <span className="font-medium text-slate-900">+{formatPercent(scenario?.breakEvenVolumeIncrease)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Profit Impact:</span>
                              <span className="font-medium text-red-700">{formatCurrencyLocal(scenario?.profitImpact)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default PromoDiscountImpactCalculator;